import { useState, useEffect } from "react";
import { Play, Pause } from "lucide-react";
import { usePodcastPlayer } from "@/context/PodcastPlayerContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useParams, useLocation } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import FullPodcastPlayer from "./FullPodcastPlayer";
import type { PodcastEpisode } from "@/types/podcast";

const FloatingPodcastPlayerBar = () => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { episodeId } = useParams();
  const { currentEpisode, isPlaying, pause, resume, playEpisode, currentTime, duration } = usePodcastPlayer();
  const [isFullPlayerOpen, setIsFullPlayerOpen] = useState(false);
  const [pageEpisode, setPageEpisode] = useState<PodcastEpisode | null>(null);

  const isEpisodePage = location.pathname.startsWith('/podcasts/');

  // Fetch current page episode if on episode page
  useEffect(() => {
    if (!episodeId || !isEpisodePage) {
      setPageEpisode(null);
      return;
    }

    const fetchEpisode = async () => {
      const { data } = await supabase
        .from('podcast_episodes')
        .select(`
          id,
          title,
          audio_url,
          image_url,
          podcast_id,
          podcast:podcast_sources(name)
        `)
        .eq('id', episodeId)
        .single();

      if (data) {
        setPageEpisode({
          ...data,
          podcast: data.podcast as any,
        } as PodcastEpisode);
      }
    };

    fetchEpisode();
  }, [episodeId, isEpisodePage]);

  // Determine which episode to show
  const displayEpisode = pageEpisode || currentEpisode;

  // Only show on mobile when there's an episode to display
  if (!isMobile || !isEpisodePage || !displayEpisode) return null;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handlePlayPause = () => {
    if (pageEpisode && currentEpisode?.id !== pageEpisode.id) {
      playEpisode(pageEpisode);
    } else {
      isPlaying ? pause() : resume();
    }
  };

  return (
    <>
      <div
        onClick={() => setIsFullPlayerOpen(true)}
        className="w-full cursor-pointer"
      >
        {/* Thin progress bar at top */}
        <div className="h-0.5 bg-background">
          <div 
            className="h-full bg-primary transition-all duration-300" 
            style={{ width: `${progress}%` }} 
          />
        </div>
        
        {/* Darker player bar */}
        <div className="bg-background border-t border-border/50 px-4 py-2.5">
          <div className="flex items-center gap-3">
            {/* Episode thumbnail */}
            <div className="w-12 h-12 rounded-md overflow-hidden bg-muted flex-shrink-0 shadow-md">
              {displayEpisode.image_url ? (
                <img
                  src={displayEpisode.image_url}
                  alt={displayEpisode.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5" />
              )}
            </div>

            {/* Episode info */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">
                {displayEpisode.title}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {displayEpisode.podcast?.name || "Podcast"}
              </p>
            </div>

            {/* Play/Pause button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePlayPause();
              }}
              className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors flex-shrink-0"
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? (
                <Pause className="w-5 h-5 fill-primary-foreground" />
              ) : (
                <Play className="w-5 h-5 ml-0.5 fill-primary-foreground" />
              )}
            </button>
          </div>
        </div>
      </div>

      <FullPodcastPlayer
        open={isFullPlayerOpen}
        onOpenChange={setIsFullPlayerOpen}
      />
    </>
  );
};

export default FloatingPodcastPlayerBar;
